import {
  CameraManager,
  useCameraStore,
  useIdentityContext,
  useUserData,
  useWebSocketContext,
} from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { useTheme } from '@flik/shared/components';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FlikRoom from '../components/FlikRoom';
import NavBar from '../components/NavBar';
import { useLivekit } from '../hooks/useLivekit';
import MagicModal from '../magic/components/MagicModal';
import { useMicrophoneStore } from '../stores/microphoneStore';

interface LiveKitTokenResponse {
  token: string;
}

const ActiveSessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useIdentityContext();
  const { provideTailwind } = useTheme();
  const { onMessageReceived } = useWebSocketContext();
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [sessionData, setSessionData] = useState<any>(null);
  const { isVideoOn: storeVideoOn } = useCameraStore();
  const { isMicOn: storeMicOn } = useMicrophoneStore();

  const apiClient = useReactQuery();
  useUserData(); // Initialize user data

  // Redirect to signin if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      const currentPath = location.pathname;
      navigate(`/signin?referrer=${encodeURIComponent(currentPath)}`);
    }
  }, [isLoggedIn, navigate, location.pathname]);

  // Fetch session details
  const fetchSession = apiClient.useQuery('get', `/v1/sessions/{session_id}`, {
    params: {
      path: {
        session_id: sessionId ?? '',
      },
    },
    enabled: isLoggedIn && !!sessionId,
  });

  // Set LiveKit URL from session data
  useEffect(() => {
    if (fetchSession.data?.item?.livekit_url) {
      setLivekitUrl(fetchSession.data.item.livekit_url);
    }
  }, [fetchSession.data]);

  useEffect(() => {
    if (fetchSession.data?.item) {
      setSessionData(fetchSession.data.item);
    }
  }, [fetchSession.data]);

  useEffect(() => {
    if (fetchSession.error) {
      console.error('Failed to fetch session:', fetchSession.error);
      setError('Session not found or access denied.');
    }
  }, [fetchSession.error]);

  // Handle session state changes - redirect to inactive view if session becomes inactive
  useEffect(() => {
    if (sessionData?.state === 'inactive') {
      console.log(
        'Session became inactive, redirecting to inactive session view',
      );
      navigate(`/sessions/${sessionId}`);
    }
  }, [sessionData?.state, navigate, sessionId]);

  // Handle WebSocket events for session ended
  useEffect(() => {
    if (!isLoggedIn || !sessionId) {
      return;
    }

    const cleanupSessionEnded = onMessageReceived(
      'onSessionEnded',
      (message) => {
        console.log(
          'ActiveSessionPage: Session ended received, checking if it affects current session:',
          message,
        );

        // Check if the ended session matches our current session
        if (message.data.sessionId === sessionId) {
          console.log(
            'ActiveSessionPage: Current session ended, redirecting to inactive view',
          );
          navigate(`/sessions/${sessionId}`);
        }
      },
    );

    return () => {
      cleanupSessionEnded();
    };
  }, [onMessageReceived, isLoggedIn, sessionId, navigate]);

  // Use LiveKit hook
  const {
    roomRef,
    isLivekitConnected,
    error: livekitError,
    disconnectFromLivekit,
    isMicOn,
    isVideoOn,
    nonCameraVideoTracks,
    visibleParticipants,
    focusedTrack,
    videoRefs,
    nonCameraVideoRefs,
    localVideoRef,
    toggleMicrophone,
    toggleVideo,
    handleExitFocusMode,
    setFocusedTrack,
    registerVideoElement,
  } = useLivekit({
    sessionId: sessionId || null,
    livekitUrl,
    isMicOn: storeMicOn, // Use microphone state from store
    isVideoOn: storeVideoOn, // Use video state from store
    isScreenSharing: false,
    isSystemSoundOn: false,
  });

  const handleLeaveSession = () => {
    disconnectFromLivekit();
    navigate('/');
  };

  // Show loading state
  if (fetchSession.isLoading || isLoading) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <p className={`${provideTailwind('text-secondary')}`}>
              {fetchSession.isLoading
                ? 'Loading session...'
                : 'Connecting to session...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || fetchSession.error || livekitError) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1
              className={`text-2xl font-bold mb-4 ${provideTailwind(
                'text-primary',
              )}`}
            >
              Connection Error
            </h1>
            <p className={`mb-6 ${provideTailwind('text-secondary')}`}>
              {error ||
                livekitError ||
                'Failed to connect to session. Please try again.'}
            </p>
            <div
              className={`${provideTailwind(
                'bg-secondary',
              )} rounded-lg p-4 mb-6 ${provideTailwind(
                'border',
              )} ${provideTailwind('border-primary')}`}
            >
              <p
                className={`text-sm font-semibold mb-2 ${provideTailwind(
                  'text-primary',
                )}`}
              >
                Troubleshooting:
              </p>
              <ul
                className={`text-sm space-y-1 ${provideTailwind(
                  'text-secondary',
                )}`}
              >
                <li>• Ensure the Flik backend server is running</li>
                <li>• Check that the session ID is correct</li>
                <li>• Verify your network connection</li>
                <li>• Make sure you have permission to join this session</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/')}
              className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show waiting for connection state
  if (!isLivekitConnected) {
    return (
      <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className={`${provideTailwind('text-secondary')}`}>
              Joining session...
            </p>
            <p className={`text-sm mt-2 ${provideTailwind('text-tertiary')}`}>
              Connecting to LiveKit room...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render the actual LiveKit room with NavBar and MagicModal
  return (
    <div className={`w-full min-h-screen ${provideTailwind('bg-primary')}`}>
      {/* Media Managers */}
      <CameraManager isVideoOn={isVideoOn} />

      {/* NavBar */}
      <NavBar />

      {/* Magic Modal at the top without initial offset */}
      <MagicModal />

      {/* LiveKit Room */}
      <FlikRoom
        screenShares={nonCameraVideoTracks.map((t) => ({
          id: t.participantIdentity + '|' + t.trackSid,
          participantIdentity: t.participantIdentity,
          trackSid: t.trackSid,
          publication: t.publication,
        }))}
        session={{
          roomRef,
          isMicOn,
          isVideoOn,
          onToggleMic: toggleMicrophone,
          onToggleVideo: toggleVideo,
          onLeaveRoom: handleLeaveSession,
          localVideoRef,
          localVideoEnabled: isVideoOn,
          visibleParticipants,
          videoRefs,
          title: sessionData?.title,
          nonCameraVideoRefs,
          registerVideoElement,
          avatarUrl: undefined,
        }}
        room={roomRef.current}
        isCursorSharingEnabled={true}
      />
    </div>
  );
};

export default ActiveSessionPage;
